// src/components/PlyrPlayer.tsx
import React, { useCallback, useEffect, useRef } from "react";

type YouTubeProps = {
  provider: "youtube";
  embedId: string;
  options?: any;
  className?: string;
  onReady?: (player: any) => void;
};

type Html5Props = {
  provider?: "html5";
  sources: { src: string; type?: string; size?: number }[];
  options?: any;
  className?: string;
  onReady?: (player: any) => void;
};

type Props = YouTubeProps | Html5Props;

declare global {
  interface Window {
    Plyr?: any;
  }
}

export default function PlyrPlayer(props: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);

  const isYouTube = (props as YouTubeProps).provider === "youtube";
  const providerType = isYouTube ? "youtube" : "html5";
  const embedId = isYouTube ? (props as YouTubeProps).embedId : undefined;
  const sources = !isYouTube ? (props as Html5Props).sources : undefined;
  const sourcesKey = sources && sources[0] ? sources[0].src : undefined;
  const options = (props as any).options;

  // Stable storage key generation
  const getStorageKey = useCallback(() => {
    if (providerType === "youtube" && embedId) {
      return `plyr_progress_youtube_${embedId}`;
    } else if (sourcesKey) {
      return `plyr_progress_html5_${sourcesKey}`;
    }
    return "plyr_progress_default";
  }, [providerType, embedId, sourcesKey]);

  const saveProgress = useCallback(
    (currentTime: number) => {
      try {
        const key = getStorageKey();
        localStorage.setItem(
          key,
          JSON.stringify({ time: currentTime, timestamp: Date.now() }),
        );
      } catch (e) {
        // ignore
      }
    },
    [getStorageKey],
  );

  const loadProgress = useCallback(() => {
    try {
      const key = getStorageKey();
      const raw = localStorage.getItem(key);
      if (raw) {
        const data = JSON.parse(raw);
        if (Date.now() - data.timestamp < 7 * 24 * 60 * 60 * 1000) {
          return data.time;
        }
        localStorage.removeItem(key);
      }
    } catch (e) {
      // ignore
    }
    return 0;
  }, [getStorageKey]);

  const clearProgress = useCallback(() => {
    try {
      const key = getStorageKey();
      localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  }, [getStorageKey]);

  useEffect(() => {
    // This assumes Plyr JS/CSS are loaded from public/index.html.
    // If Plyr isn't available immediately (dev hot-reload), retry a few times.
    let didUnmount = false;
    let retryTimer: number | null = null;

    const tryInit = () => {
      if (didUnmount) return;
      if ((window as any).Plyr) {
        initPlayer();
      } else {
        retryTimer = window.setTimeout(tryInit, 200);
      }
    };
    tryInit();

    function initPlayer() {
      const PlyrCtor = (window as any).Plyr;
      if (!PlyrCtor) {
        console.warn("Plyr missing on window");
        return;
      }

      // Destroy existing instance first
      if (playerRef.current?.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          // ignore
        }
        playerRef.current = null;
      }

      if (!wrapRef.current) return;

      let attachEl: Element | null = null;
      if (providerType === "youtube" && embedId) {
        attachEl = wrapRef.current.querySelector(
          `div[data-plyr-provider="youtube"][data-plyr-embed-id="${embedId}"]`,
        );
      } else {
        attachEl = wrapRef.current.querySelector("video");
      }

      if (!attachEl) {
        console.warn("Plyr attach element not found", { providerType, embedId, sourcesKey });
        return;
      }

      // Minimal default controls: only volume/mute (no play/pause, no progress)
      const minimalControls = ["mute", "volume", "captions", "settings"];

      const defaultOptions = {
        // do NOT include play / progress controls here
        controls: minimalControls,
        settings: ["captions", "quality"],
        speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5] },
        quality: { default: 720, options: [1080, 720, 480], forced: false },
        keyboard: { global: false, focused: false },
        clickToPlay: false, // important: clicking the player won't toggle play/pause
        hideControls: false,
        autoplay: false,
        autoplayFallback: false,
        youtube: {
          noCookie: true,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          widget_referrer: window.location.href,
        },
      };

      // Merge with user options (user options override defaults)
      const mergedOptions = { ...defaultOptions, ...(options || {}) };
      if (options?.youtube) mergedOptions.youtube = { ...defaultOptions.youtube, ...options.youtube };

      try {
        playerRef.current = new (window as any).Plyr(attachEl as any, mergedOptions);

        const savedTime = loadProgress();
        let restored = false;

        // READY
        playerRef.current.on("ready", () => {
          // restore playback position (if any)
          if (savedTime > 0 && !restored) {
            setTimeout(() => {
              try {
                playerRef.current.currentTime = savedTime;
                restored = true;
              } catch (e) {
                // ignore
              }
            }, 800);
          }

          // forward to parent
          try {
            props.onReady?.(playerRef.current);
          } catch (e) {
            // ignore
          }

          // Immediately attempt to make audio audible (best-effort).
          // We'll also retry once after a short delay because embed internals may be delayed.
          const forceUnmuteAndVolume = () => {
            try {
              // Plyr API
              if (typeof playerRef.current.setVolume === "function") {
                playerRef.current.setVolume(1); // 1.0 = 100%
              } else if (typeof playerRef.current.volume === "number") {
                playerRef.current.volume = 1;
              }
              if (typeof playerRef.current.muted !== "undefined") {
                playerRef.current.muted = false;
              }
            } catch (e) {
              // ignore
            }
            // embed (YouTube) fallback
            try {
              const embed = playerRef.current?.embed;
              if (embed) {
                if (typeof embed.unMute === "function") embed.unMute();
                if (typeof embed.setVolume === "function") embed.setVolume(100); // 0-100 scale
              }
            } catch (e) {
              // ignore
            }
          };

          // try now + retry slightly later
          forceUnmuteAndVolume();
          setTimeout(forceUnmuteAndVolume, 300);
        });

        // Save progress on pause/seek/timeupdate/ended
        playerRef.current.on("pause", () => {
          try {
            saveProgress(playerRef.current.currentTime);
          } catch (e) {}
        });

        playerRef.current.on("seeked", () => {
          try {
            saveProgress(playerRef.current.currentTime);
          } catch (e) {}
        });

        playerRef.current.on("timeupdate", () => {
          try {
            if (!playerRef.current.paused) {
              const ct = Math.floor(playerRef.current.currentTime || 0);
              if (ct > 0 && ct % 5 === 0) saveProgress(playerRef.current.currentTime);
            }
          } catch (e) {}
        });

        playerRef.current.on("ended", () => {
          try {
            clearProgress();
          } catch (e) {}
        });

        // helpful: if embed later raises volumechange we could forward to parent, but not necessary
      } catch (err) {
        console.error("Plyr init error", err);
      }
    }

    return () => {
      didUnmount = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }

      if (playerRef.current) {
        try {
          if (!playerRef.current.ended && playerRef.current.currentTime > 0) {
            saveProgress(playerRef.current.currentTime);
          }
        } catch (e) {}
        try {
          if ((playerRef.current as any)._cleanupBeforeUnload) (playerRef.current as any)._cleanupBeforeUnload();
        } catch (e) {}
        try {
          playerRef.current.destroy();
        } catch (e) {}
      }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerType, embedId, sourcesKey, options, saveProgress, loadProgress, clearProgress]);

  // Render structure Plyr expects
  if (isYouTube) {
    const id = embedId!;
    return (
      <div ref={wrapRef} className={props.className || ""}>
        <div data-plyr-provider="youtube" data-plyr-embed-id={id} />
      </div>
    );
  }

  // HTML5 fallback
  return (
    <div ref={wrapRef} className={props.className || ""}>
      <video controls crossOrigin="anonymous" style={{ width: "100%" }}>
        {(sources || []).map((s: any, i: number) => (
          <source key={i} src={s.src} type={s.type || "video/mp4"} />
        ))}
        Your browser does not support the video tag.
      </video>
    </div>
  );
}